import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BrainCircuit, Send } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGGESTIONS = [
  "I have completed Class 12 and want a career in AI.",
  "Show me short-term courses after Class 12.",
  "I want a six-month data course.",
  "Which course should I take to learn Python?",
  "I want a B.Voc program in IT.",
];

export default function CareerAI() {
  const [sessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello! I'm XTM Career AI — the career guidance assistant of XTM TECHNOLOGIES, Jorhat. Tell me about your education, interests and career goal, and I'll recommend programs from our actual course catalogue. I never guarantee jobs — only honest guidance." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", courses: [] }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/career-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let event;
          try { event = JSON.parse(line.slice(6)); } catch { continue; }
          if (event.type === "token") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + event.content };
              return copy;
            });
          } else if (event.type === "courses") {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, courses: [...(last.courses || []), ...event.items] };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content || "Connection issue — please try again or WhatsApp us at +91 9101380454." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="bg-navy-base min-h-screen" data-testid="career-ai-page">
      <SEO title="XTM Career AI — AI Career Counsellor | XTM TECHNOLOGIES Jorhat"
        description="Chat with XTM Career AI for course recommendations from the real XTM course database — AI, data, cybersecurity, Tally, B.Voc and more." />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-orange-600 flex items-center justify-center shrink-0">
            <BrainCircuit className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white" data-testid="career-ai-heading">XTM Career AI</h1>
            <p className="text-xs text-slate-400">Recommends only real XTM courses • Guidance, not job guarantees</p>
          </div>
        </div>

        <div className="bg-card border border-white/10 rounded-3xl overflow-hidden flex flex-col" style={{ height: "62vh" }}>
          <div className="flex-1 overflow-y-auto p-5 sm:p-7 space-y-5" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-orange-600 text-white" : "bg-navy-deep border border-white/10 text-slate-200"}`}
                  data-testid={`chat-message-${i}`}>
                  {m.content}
                  {streaming && i === messages.length - 1 && m.role === "assistant" && !m.content && (
                    <span className="text-slate-500 animate-pulse">Thinking…</span>
                  )}
                  {m.courses?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {[...new Map(m.courses.map((c) => [c.slug, c])).values()].map((c) => (
                        <Link key={c.slug} to={`/course/${c.slug}`}
                          className="block bg-navy-surface border border-white/10 hover:border-orange-500/50 rounded-xl p-3 transition-colors"
                          data-testid={`chat-course-${c.slug}`}>
                          <div className="font-semibold text-white text-sm">{c.title}</div>
                          <div className="text-xs text-slate-400">{c.duration} • {c.level} • {c.mode} • ₹{Number(c.fees).toLocaleString("en-IN")}</div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-white/10 p-4">
            <div className="flex flex-wrap gap-2 mb-3">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} disabled={streaming}
                  className="text-[11px] text-slate-400 border border-white/15 hover:border-orange-500/40 hover:text-orange-300 rounded-full px-3 py-1.5 transition-colors disabled:opacity-40"
                  data-testid={`chat-suggestion-${SUGGESTIONS.indexOf(s)}`}>
                  {s}
                </button>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)}
                placeholder="e.g. I am a Class 12 student interested in AI and want a career-oriented course…"
                className="flex-1 bg-navy-deep border border-white/15 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-orange-500/60"
                data-testid="chat-input" />
              <Button type="submit" disabled={streaming || !input.trim()} className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl px-5 transition-colors" data-testid="chat-send-btn">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
